import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Autocomplete,
  Typography,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useNotify } from '@/stores/notificationStore';
import { useCreateShiftOverride, type ComputedShift, type CreateOverrideData } from '@/hooks/useShifts';
import { useMechanics } from '@/hooks/useStaffUsers';

interface ShiftOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  shift: ComputedShift | null;
}

export function ShiftOverrideDialog({ open, onClose, shift }: ShiftOverrideDialogProps) {
  const { t } = useTranslation('shifts');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const createOverride = useCreateShiftOverride();
  const { data: mechanics = [] } = useMechanics();
  const { isDirty, markDirty } = useFormDirty(open);

  const [overrideType, setOverrideType] = useState<'custom_time' | 'day_off' | 'swap'>('custom_time');
  const [startTime, setStartTime] = useState(shift?.startTime ?? '07:00');
  const [endTime, setEndTime] = useState(shift?.endTime ?? '15:00');
  const [swapWithUserId, setSwapWithUserId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    if (!shift) return;

    const data: CreateOverrideData = {
      userId: shift.userId,
      date: shift.date,
      overrideType,
      reason: reason || undefined,
    };

    if (overrideType === 'custom_time') {
      data.startTime = startTime;
      data.endTime = endTime;
    }

    if (overrideType === 'swap' && swapWithUserId) {
      data.swapWithUserId = swapWithUserId;
    }

    try {
      await createOverride.mutateAsync(data);
      notify.success(t('overrideCreated'), t('overrideCreatedDesc'));
      onClose();
    } catch {
      notify.error(tc('error'), t('errorSaving'));
    }
  };

  if (!shift) return null;

  const swapCandidates = mechanics.filter((m) => m.id !== shift.userId);

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('createOverride')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      isLoading={createOverride.isPending}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {shift.date} | {shift.startTime} - {shift.endTime} | {shift.groupName}
          </Typography>

          <FormControl fullWidth>
            <InputLabel>{t('overrideType')}</InputLabel>
            <Select
              value={overrideType}
              label={t('overrideType')}
              onChange={(e) => { setOverrideType(e.target.value as typeof overrideType); markDirty(); }}
            >
              <MenuItem value="custom_time">{t('customTime')}</MenuItem>
              <MenuItem value="day_off">{t('dayOff')}</MenuItem>
              <MenuItem value="swap">{t('swap')}</MenuItem>
            </Select>
          </FormControl>

          {overrideType === 'custom_time' && (
            <Stack direction="row" spacing={2}>
              <TextField
                label={t('startTime')}
                type="time"
                value={startTime}
                onChange={(e) => { setStartTime(e.target.value); markDirty(); }}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label={t('endTime')}
                type="time"
                value={endTime}
                onChange={(e) => { setEndTime(e.target.value); markDirty(); }}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          )}

          {overrideType === 'swap' && (
            <Autocomplete
              options={swapCandidates}
              getOptionLabel={(m) => `${m.firstName} ${m.lastName}`}
              value={swapCandidates.find((m) => m.id === swapWithUserId) ?? null}
              onChange={(_, val) => { setSwapWithUserId(val?.id ?? null); markDirty(); }}
              renderInput={(params) => (
                <TextField {...params} label={t('swapWith')} />
              )}
            />
          )}

          <TextField
            label={t('reason')}
            value={reason}
            onChange={(e) => { setReason(e.target.value); markDirty(); }}
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
